/**
 * Where every control on the map sits — ours and Leaflet's, in one table.
 *
 * THE BUG THIS EXISTS TO END. Our overlay lived in a `position: absolute` strip of its own
 * (`.k-maptop`), stretched from the roster to the console, with the date control pushed to
 * its right end. Leaflet's zoom buttons were at `topright`, inset by the same console width.
 * Two independent layout systems aiming at the same pixels: the date bar painted over the
 * zoom buttons, and which one won was a z-index accident (`.leaflet-top` is 1000,
 * `.k-maptop` was 400) rather than a decision.
 *
 * So there is one layout system now, and it is Leaflet's documented one: `L.Control` with a
 * `position`, added to a corner, stacked inside that corner by Leaflet's own flow. Controls
 * in one corner CANNOT overlap — the corner is a normal block container and they are floated
 * blocks with `clear: both`. Controls in different corners cannot overlap either, as long as
 * the table below keeps ours and theirs apart, which is what `controls.test.ts` asserts.
 *
 * The corners are inset by the two panels (styles/layout.css: `.leaflet-left` /
 * `.leaflet-right`), because the roster and the console are siblings of the map and cover
 * its edges. That inset is stated once, on the corner, and not per control.
 */

import * as L from "leaflet";

export type MapCorner = "topleft" | "topright" | "bottomleft" | "bottomright";

/** The one place that answers "what is in this corner". */
export interface ControlPlacement {
  /** Stable id, used by the test and by the report — not shown to anyone. */
  id: string;
  corner: MapCorner;
  /** False for Leaflet's own chrome: zoom, scale, attribution. */
  ours: boolean;
  /** What a person sees there. */
  what: string;
}

export const CONTROL_LAYOUT: ControlPlacement[] = [
  {
    id: "tools",
    corner: "topleft",
    ours: true,
    what: "контур: рисование, импорт, удаление, лист черновика; поиск по координатам; подложка",
  },
  // Тумблера в этом описании больше нет вместе с самим тумблером: включает слой любой клик
  // по дате, выключает точка отметки (components/PreviewControl.tsx).
  {
    id: "imagery",
    corner: "topright",
    ours: true,
    what: "снимок на дату: отметка, стрелки, календарь",
  },
  // Moved off `topright`, where it was under the date bar. Bottom-left is the one corner
  // nothing of ours will ever want: the roster covers that edge of the screen, the drawing
  // tools are at the top, and the scale and the attribution are at the other end.
  { id: "zoom", corner: "bottomleft", ours: false, what: "зум Leaflet" },
  // Together, because both are statements about the picture rather than instruments: the
  // scale earns its pixels here on its own, Web Mercator stretching it 2.9× between 44° and
  // 70° N, and the attribution is a licence condition for both providers.
  { id: "scale", corner: "bottomright", ours: false, what: "масштабная линейка" },
  { id: "attribution", corner: "bottomright", ours: false, what: "атрибуция подложки" },
];

export const cornerOf = (id: string): MapCorner | null =>
  CONTROL_LAYOUT.find((placement) => placement.id === id)?.corner ?? null;

/** Corners holding both a control of ours and one of Leaflet's. Must always be empty. */
export const contestedCorners = (): MapCorner[] => {
  const ours = new Set(CONTROL_LAYOUT.filter((item) => item.ours).map((item) => item.corner));
  const theirs = CONTROL_LAYOUT.filter((item) => !item.ours).map((item) => item.corner);
  return [...new Set(theirs.filter((corner) => ours.has(corner)))];
};

export interface MapSlot {
  element: HTMLElement;
  remove: () => void;
}

/**
 * An empty `L.Control` in a corner, for React to render into.
 *
 * The two `DomEvent` calls are not decoration. Our panels are INSIDE `.leaflet-container`
 * now, so without them a click on the save button is also a click on the map — which in draw
 * mode plants a vertex — and a wheel over the calendar zooms the map underneath it. The main
 * product hit exactly this and fixed it the same way
 * (`frontend/src/components/ui/SatelliteDateNav.tsx`).
 *
 * `disableClickPropagation` also stops `mousedown` reaching the container, which is what
 * keeps Leaflet's keyboard handler asleep: it hooks `document` only while the container
 * itself has focus (`Map.Keyboard._onMouseDown` → `container.focus()`), so typing an arrow
 * key into the coordinate field pans nothing.
 */
export const mapSlot = (map: L.Map, corner: MapCorner, className: string): MapSlot => {
  const control = new L.Control({ position: corner });
  control.onAdd = (): HTMLElement => {
    const element = L.DomUtil.create("div", `k-mapctl ${className}`);
    L.DomEvent.disableClickPropagation(element);
    L.DomEvent.disableScrollPropagation(element);
    return element;
  };
  control.addTo(map);
  const element = control.getContainer();
  if (!element) throw new Error("mapSlot: control has no container");
  return { element, remove: () => control.remove() };
};

export const SLOT_CLASS = {
  tools: "k-mapctl--tools",
  imagery: "k-mapctl--imagery",
} as const;
